import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';

const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

// Legacy ProAcenta "Mizan Şekli": cumulative-to-month-end, single month, or a
// free date range. Shared by the cari mizan and the accounting trial balances.
export type MizanMode = 'cumulative' | 'monthly' | 'range';

export interface MizanPeriod {
  mode: MizanMode;
  year: number;
  month: number; // 0-based
  startDate: string; // yyyy-mm-dd, '' = from the beginning (range mode only)
  endDate: string; // yyyy-mm-dd
}

export function defaultMizanPeriod(): MizanPeriod {
  const now = new Date();
  return {
    mode: 'cumulative',
    year: now.getFullYear(),
    month: now.getMonth(),
    startDate: '',
    endDate: now.toISOString().split('T')[0] ?? '',
  };
}

// Ledger dates are stored at UTC midnight — build UTC month bounds so the
// first/last day of the month are included exactly.
export function mizanPeriodToDates(p: MizanPeriod): { start: Date | undefined; end: Date } {
  if (p.mode === 'range') {
    return {
      start: p.startDate ? new Date(p.startDate) : undefined,
      end: p.endDate ? new Date(p.endDate) : new Date(),
    };
  }
  const monthEnd = new Date(Date.UTC(p.year, p.month + 1, 0));
  if (p.mode === 'monthly') return { start: new Date(Date.UTC(p.year, p.month, 1)), end: monthEnd };
  return { start: undefined, end: monthEnd }; // cumulative: from the beginning
}

const FIRST_FISCAL_YEAR = 2008; // earliest legacy fiscal year across both apps

interface MizanPeriodPickerProps {
  value: MizanPeriod;
  onChange: (p: MizanPeriod) => void;
  /** i18n namespace holding the labels (keys under `${keyPrefix}.`) */
  ns: string;
  keyPrefix: string;
  /** Lock the period to one fiscal year (hides the year select) — used by the
   * travel cari views where each legacy fiscal year is self-contained. */
  fixedYear?: number;
}

export function MizanPeriodPicker({ value, onChange, ns, keyPrefix, fixedYear }: MizanPeriodPickerProps) {
  const { t, i18n } = useTranslation(ns);
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const k = (key: string, fallback: string) => t(`${keyPrefix}.${key}`, fallback);

  const monthName = (m: number) => new Date(2000, m, 1).toLocaleDateString(dateLocale, { month: 'long' });
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear + 1 - FIRST_FISCAL_YEAR + 1 }, (_, i) => FIRST_FISCAL_YEAR + i);

  const modeOptions: { id: MizanMode; label: string }[] = [
    { id: 'cumulative', label: k('cumulative', 'Cumulative (Kümülatif)') },
    { id: 'monthly', label: k('monthly', 'Monthly (Aylık)') },
    { id: 'range', label: k('dateRange', 'Date Range (Tarih Aralığı)') },
  ];

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1">
        <Label className="text-xs">{k('reportForm', 'Report Form')}</Label>
        <div className="flex gap-1">
          {modeOptions.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange({ ...value, mode: m.id })}
              className={`h-8 border px-3 text-sm transition-colors ${
                value.mode === m.id
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-background hover:bg-muted'
              }`}
              aria-pressed={value.mode === m.id}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {value.mode !== 'range' ? (
        <>
          <div className="space-y-1">
            <Label className="text-xs">{k('month', 'Month')}</Label>
            <select
              value={value.month}
              onChange={(e) => onChange({ ...value, month: Number(e.target.value) })}
              className={`${selectClass} block w-32`}
            >
              {Array.from({ length: 12 }, (_, m) => (
                <option key={m} value={m}>{monthName(m)}</option>
              ))}
            </select>
          </div>
          {fixedYear === undefined && (
            <div className="space-y-1">
              <Label className="text-xs">{k('year', 'Year')}</Label>
              <select
                value={value.year}
                onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}
                className={`${selectClass} block w-24`}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="space-y-1">
            <Label className="text-xs">{k('startDate', 'Start Date')}</Label>
            <input
              type="date"
              value={value.startDate}
              onChange={(e) => onChange({ ...value, startDate: e.target.value })}
              className={`${selectClass} block w-40`}
              aria-label={k('startDate', 'Start Date')}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{k('endDate', 'End Date')}</Label>
            <input
              type="date"
              value={value.endDate}
              onChange={(e) => onChange({ ...value, endDate: e.target.value })}
              className={`${selectClass} block w-40`}
              aria-label={k('endDate', 'End Date')}
            />
          </div>
        </>
      )}
    </div>
  );
}
